import { FC } from 'react'
import ApplicationStore, { EditMode } from './ApplicationStore'
import details from './flow/bank/BankDetailsStep'
import businessAddress from './flow/businessInfo/businessAddress'
import businessName from './flow/businessInfo/businessName'
import businessPhone from './flow/businessInfo/businessPhone'
import category from './flow/businessInfo/category'
import ein from './flow/businessInfo/ein'
import email from './flow/businessInfo/email'
import startDate from './flow/businessInfo/startDate'
import trade from './flow/businessInfo/trade'
import type from './flow/businessInfo/type'
import address from './flow/businessOwner/address'
import ownershipPercentage from './flow/businessOwner/ownershipPercentage'
import authorisedDetails from './flow/businessOwner/authorisedDetails'
import birthdate from './flow/businessOwner/birthdate'
import isAuthorised from './flow/businessOwner/isAuthorised'
import isOwner from './flow/businessOwner/isOwner'
import ssn from './flow/businessOwner/ssn'
import debt from './flow/finance/debt'
import howMuchCredit from './flow/finance/howMuchCredit'
import revenue from './flow/finance/revenue'
import preview from './flow/review/preview'
import review from './flow/review/review'
import coOwners from './flow/coOwnerInfo/coOwners'

interface Flow {
  [key: string]: Group
}

interface Group {
  [key: string]: FlowStep
}

type FlowStep = BasicEditor | Editor | (() => string)

// provides metadata about component to be rendered in Editor (a kind of master page)
export interface BasicEditor {
  title?: string | (() => string)
  description?: string
  descriptionStyle?: any
  decidingQuestion?: boolean
  showFooterMessage?: boolean
  canMoveNext?: boolean
  canSkip?: boolean
  // return true if back is completely handled by an editor itself, return false to let host editor handle back navigation
  handleBack?: () => boolean
  component: FC<any> | ((props: any) => JSX.Element)
}

// wrapper for component with life-cycle events
interface Editor {
  editor: BasicEditor
  beforeMoveNext?: () => void
  onSkip?: string | (() => void | string)
}

export interface ModelUpdateInfo {
  value: any
  filled: boolean
  group: string
  identifier: string
}

// a kind of MVC controller for application wizard, returns components to be rendered in Editor
// based on current step. Flow consists of components and choices. Normal execution flow is from top to bottom
// group by group, choice may redirect execution to another branch
export class FlowController {
  document: any

  constructor(document: any) {
    this.document = document
  }

  private flow: Flow = {
    businessInfo: {
      firstStepChoice: () => ApplicationStore.firstQuestion,
      email: {
        editor: email,
        beforeMoveNext: () => ApplicationStore.updateUserEmail(),
      },
      category: {
        editor: category,
        beforeMoveNext: () => ApplicationStore.updateCompanyCategory(),
      },
      categoryChoice: () =>
        ApplicationStore.isSubContractor
          ? 'businessInfo.trade'
          : 'businessInfo.businessName',
      trade,
      businessName,
      businessPhone,
      businessAddress,
      startDate,
      type,
      ein,
    },

    finance: {
      revenue,
      revenueChoice: () =>
        ApplicationStore.isCreditApplication
          ? 'finance.debt'
          : 'businessOwner.isOwner',
      debt,
      debtChoice: () =>
        ApplicationStore.isPrequalifiedApplication
          ? 'finance.howMuchCredit'
          : 'businessOwner.isOwner',
      howMuchCredit,
    },

    businessOwner: {
      alreadySubmittedChoice: () =>
        ApplicationStore.isGetPaidSubmitted
          ? 'businessOwner.address'
          : 'businessOwner.isOwner',
      isOwner: {
        editor: isOwner,
        onSkip: 'bank.details',
      },
      isOwnerChoice: () =>
        ApplicationStore.isOwner
          ? 'businessOwner.ownershipPercentage'
          : 'businessOwner.isAuthorised',
      ownershipPercentage,
      goto: () => 'businessOwner.address',
      isAuthorised: {
        editor: isAuthorised,
        onSkip: 'bank.details',
      },
      isAuthorisedChoice: () =>
        ApplicationStore.isAuthorised
          ? 'businessOwner.address'
          : 'businessOwner.authorisedDetails',
      authorisedDetails: {
        editor: authorisedDetails,
        beforeMoveNext: () => ApplicationStore.inviteUser('authorisedDetails'),
      },
      address,
      birthdate,
      ssn,
    },

    coOwnerInfo: {
      coOwnersChoice: () =>
        ApplicationStore.canHaveCoOwners || ApplicationStore.hasCoOwners
          ? 'coOwnerInfo.coOwners'
          : 'bank.details',
      coOwners,
    },

    bank: {
      details: {
        editor: details,
        beforeMoveNext: () => ApplicationStore.updatePrimaryBankAccount(),
      },
    },

    review: {
      review,
      preview,
    },
  }

  moveToNextStep(updateInfo: ModelUpdateInfo, groupTitle: string) {
    this.updateDocument(updateInfo, groupTitle)
    this.doTransitionLogic()

    const targetPath = this.findNextStep(this.document.current)
    if (targetPath) this.updateDocumentCurrentPath(targetPath)
  }

  skipToNextStep(updateInfo: ModelUpdateInfo, groupTitle: string) {
    this.updateDocument(updateInfo, groupTitle)
    const currentStep = this.findStepByPath(this.document.current)

    if (currentStep && 'onSkip' in currentStep) {
      const onSkip = currentStep.onSkip
      if (onSkip instanceof Function) {
        const skipToPath = onSkip()
        if (skipToPath) {
          this.document.setCurrent(skipToPath)
        }
      } else if (onSkip) {
        this.document.setCurrent(onSkip)
      }
    } else {
      const targetPath = this.findNextStep(this.document.current)
      if (targetPath) this.updateDocumentCurrentPath(targetPath)
    }
  }

  moveToPreviousStep(updateInfo: ModelUpdateInfo, groupTitle: string) {
    this.updateDocument(updateInfo, groupTitle)

    const flowSteps = this.getFlowSteps(true)
    const currentStepIndex = flowSteps.indexOf(this.document.current)

    if (currentStepIndex > 0) {
      this.document.setCurrent(flowSteps[currentStepIndex - 1])
    } else {
      this.document.setCurrent('review.review')
    }
  }

  canMoveToPreviousStep(): boolean {
    // when flow is passed till review, Back on first step will lead to review
    const flowCompleted =
      this.document?.data
        ?.get('review')
        ?.items?.some((i) => i.identifier === 'review') ||
      this.document?.filled?.includes('review.review')

    return (
      this.document?.current !== 'review.review' &&
      (flowCompleted ||
        (this.document?.current &&
          this.document?.current !== this.getInitialStep()))
    )
  }

  editSection(section: string, fromBeginning = true) {
    const group = this.document?.data.get(section)
    const initial = this.getFirstGroupStep(section)

    if (fromBeginning || !group || group.allFilled) {
      ApplicationStore.goToQuestion(initial, this.document)
    } else {
      const id = ApplicationStore.firstSkipped(section)
      this.document?.setSection(`${section}.${id}`)
    }
  }

  updateDocument(updateInfo: ModelUpdateInfo, groupTitle: string) {
    ApplicationStore.handleDocumentUpdate(
      updateInfo.value,
      updateInfo.identifier,
      updateInfo.group,
      updateInfo.filled,
      groupTitle,
    )
  }

  getInitialStep(): string {
    const firstGroup = Object.keys(this.flow)[0]
    return this.getFirstGroupStep(firstGroup)
  }

  findNextStep(path: string, honorEditMode = true): string {
    const [currentGroupName, currentStepName] = path.split('.')

    const currentGroup = this.flow[currentGroupName]
    const stepNames = Object.keys(currentGroup)
    const stepIndex = stepNames.indexOf(currentStepName)

    let targetGroupName = currentGroupName
    let targetStepName: string | undefined

    // if current group has more steps, go to next one,
    // otherwise go to the first step of the next group
    if (stepIndex < stepNames.length - 1) {
      targetStepName = stepNames[stepIndex + 1]
    } else {
      const groupNames = Object.keys(this.flow)
      const groupIndex = groupNames.indexOf(currentGroupName)

      if (groupIndex < groupNames.length - 1) {
        targetGroupName = groupNames[groupIndex + 1]
        targetStepName = Object.keys(this.flow[targetGroupName])[0]
      }
    }

    if (!targetStepName) return ''

    // if target step is conditional step, execute flow till first Editor step
    const targetPath = this.tryHandleConditionalJumps(
      targetGroupName,
      targetStepName,
    )

    const skipFilled = [
      EditMode.SKIP_FILLED,
      EditMode.SKIP_FILLED_STRICT,
    ].includes(ApplicationStore.editMode)

    if (honorEditMode && skipFilled) {
      if (
        ApplicationStore.editMode === EditMode.SKIP_FILLED_STRICT ||
        !['finance.debt', 'finance.howMuchCredit', 'bank.details'].includes(
          targetPath,
        )
      ) {
        const [group, identifier] = targetPath.split('.')
        if (ApplicationStore.isFilled(group, identifier)) {
          return this.findNextStep(targetPath)
        }
      }
    }

    return targetPath
  }

  findComponent(group: string, identifier: string): BasicEditor | undefined {
    return this.findComponentByPath(`${group}.${identifier}`)
  }

  findComponentByPath(path: string): BasicEditor | undefined {
    const step = this.findStepByPath(path)

    if (step === undefined || step instanceof Function) {
      return undefined
    } else if ('editor' in step) {
      return step.editor
    } else {
      return step
    }
  }

  getProgress(): number {
    const flowSteps = this.getFlowSteps()
    const totalFilled = flowSteps.reduce((filledCount: number, step) => {
      const [group, identifier] = step.split('.')
      return ApplicationStore.isFilled(group, identifier)
        ? filledCount + 1
        : filledCount
    }, 0)

    return totalFilled / flowSteps.length
  }

  getGroupNames(): string[] {
    return Object.keys(this.flow).filter((groupName) => groupName !== 'review')
  }

  getFirstGroupStep(groupName: string): string {
    const firstStepName = Object.keys(this.flow[groupName])[0]
    return this.tryHandleConditionalJumps(groupName, firstStepName)
  }

  getFlowSteps(includeReview = false): string[] {
    // returns list of possible flow steps available for current choices (isOwner, isAuthorized, revenue amount etc.)
    let step = ApplicationStore.firstQuestion
    const steps: string[] = []

    while (step && (includeReview || step !== 'review.review')) {
      steps.push(step)
      step = this.findNextStep(step, false)
    }

    return steps
  }

  getGroupSteps(group: string): string[] {
    let step = this.getFirstGroupStep(group)
    const steps: string[] = []

    while (step && step.startsWith(`${group}.`)) {
      steps.push(step)
      step = this.findNextStep(step, false)
    }

    return steps
  }

  private updateDocumentCurrentPath(targetPath: string) {
    // user reached the flow end - reset edit mode
    if (targetPath === 'review.review') {
      ApplicationStore.setEditMode(EditMode.NORMAL)
    }

    this.document?.setCurrent(targetPath)
  }

  private tryHandleConditionalJumps(
    targetGroup: string,
    targetStep: string,
  ): string {
    const step = this.flow[targetGroup][targetStep]

    if (step instanceof Function) {
      const gotoPath = step()
      const [groupName, stepName] = gotoPath.split('.')

      return this.tryHandleConditionalJumps(groupName, stepName)
    } else {
      return `${targetGroup}.${targetStep}`
    }
  }

  private doTransitionLogic() {
    const step = this.findStepByPath(this.document.current)

    if (step && 'beforeMoveNext' in step && step.beforeMoveNext) {
      step.beforeMoveNext()
    }
  }

  private findStepByPath(path: string): FlowStep | undefined {
    const [groupName, stepName] = path.split('.')
    return this.flow[groupName][stepName]
  }
}
